import { Controller } from "@hotwired/stimulus";

import {
  isPwa,
  isPwaInstalled,
  isAndroid,
  isAndroidGoogleChrome,
  isiOS,
  logDeviceInfo,
} from "helpers/pwa_utils";
import { makeLogger } from "helpers/pwa_logger";

const baseLogger = makeLogger("pwa_install");
let log = baseLogger;

export default class extends Controller {
  static targets = [
    "androidPostInstallBanner",
    "androidNonChromeCloseButton",
    "androidNonChromeOverlay",
    "androidNonChromePanel",
    "installBanner",
    "fallbackNotice",
    "iosCloseButton",
    "iosOverlay",
    "iosPanel",
    "openAppBanner",
    "installContainer",
  ];
  static values = {
    animationDuration: Number,
    customerAppEarlyAccess: Boolean,
  };

  async connect() {
    log("controller connected");
    logDeviceInfo(log);

    // Do this immediately so we're listening right away
    this.addBeforeInstallPromptListener();

    if (await isPwa()) {
      log("Running as PWA");
      // TODO: if on instal page change install button to open app button
    } else {
      if (isiOS()) {
        log("iOS detected. Showing install banner.");
        this.installBannerTarget.classList.remove("hidden");
        this.installContainerTarget.classList.remove("hidden");
      } else if (isAndroid()) {
        log("Android detected");
        if (!isAndroidGoogleChrome()) {
          log("Non-Chrome Android browser detected. Showing install banner.");
          this.installBannerTarget.classList.remove("hidden");
          this.installContainerTarget.classList.remove("hidden");
        } else if (await isPwaInstalled(log)) {
          log("Installed app detected. Showing open link.");
          this.installBannerTarget.classList.add("hidden");
          this.installContainerTarget.classList.remove("hidden");
          this.openAppBannerTarget.classList.remove("hidden");
        } else {
          if (window.deferredPrompt) {
            // since we're using turbo, we're not doing full page loads between pages and this event
            // may have already happened and been captured by `register_portal_service_worker.js` on
            // a previous page before this controller was wired up.
            log("deferredPrompt already present. Showing Android install.");
            this.installBannerTarget.classList.remove("hidden");
            this.installContainerTarget.classList.remove("hidden");
          } else {
            log("Waiting for beforeinstallprompt to fire");
          }
        }
      } else if (this.customerAppEarlyAccessValue) {
        log("Customer app early access enabled. Showing install banner.");
        this.installContainerTarget.classList.remove("hidden");
      } else {
        log("Not on iOS or Android, showing QR code fallback notice");
        this.installContainerTarget.classList.remove("hidden");
        // a class of "md:block" is will display the fallbackNoticeTarget on desktop-sized viewports
      }
    }
  }

  addBeforeInstallPromptListener() {
    log("Adding beforeinstallprompt listener");
    window.addEventListener("beforeinstallprompt", (e) => {
      // Note: We call `preventDefault` on this event in `register_portal_service_worker.js`
      log("Received beforeinstallprompt in pwa_install_controller");
      if (isAndroid()) {
        log("Android detected in beforeinstallprompt listener. Showing install banner.");
        window.deferredPrompt = e;
        this.installBannerTarget.classList.remove("hidden");
        this.installContainerTarget.classList.remove("hidden");
      } else {
        log("Android not detected in beforeinstallprompt listener. Not showing install banner.");
      }
    });
  }

  // Main install action called from button click
  install() {
    log("Install button clicked");

    // Check if Android installation is possible
    if (isiOS()) {
      log("Showing iOS guide");
      this.#showiOSGuide();
    } else if (isAndroid()) {
      if (isAndroidGoogleChrome()) {
        log("Showing Android installation");
        this.#showAndroidInstall();
      } else {
        log("Showing Chrome redirect");
        this.#showAndroidNonChromeGuide();
      }
    } else {
      alert("Only available on iOS and Android devices");
      log("Not on iOS or Android, not installing.");
    }
  }

  // Handle Android installation prompt
  async #showAndroidInstall() {
    if (!window.deferredPrompt) {
      log("No deferred prompt available");
      alert("Unable to install, Android device not detected");
      return;
    }

    try {
      log("Showing Android install prompt");
      window.deferredPrompt.prompt();

      // Wait for the user to respond to the prompt
      log("Waiting for user choice...");
      const choiceResult = await window.deferredPrompt.userChoice;

      log("User choice:", choiceResult.outcome);
      if (choiceResult.outcome === "accepted") {
        log("Installation accepted");
        this.installBannerTarget.classList.add("hidden");
        this.androidPostInstallBannerTarget.classList.remove("hidden");
        this.#checkForInstallationAndroid();
      } else {
        log("Installation declined");
      }
    } catch (error) {
      log("Install prompt error:", error);
    } finally {
      // Clear the saved prompt - it can only be used once.
      log("Clearing deferred prompt");
      window.deferredPrompt = null;
      setTimeout(() => {
        // We should immediately get another `beforeinstallprompt`, but if not, hide the install
        // banner until we do.
        if (!window.deferredPrompt) {
          log("No new deferredPrompt set, hiding install banner");
          this.installBannerTarget.classList.add("hidden");
          this.installContainerTarget.classList.add("hidden");
        }
      }, 250);
    }
  }

  #showiOSGuide() {
    log("Showing iOS installation guide");
    this.iosOverlayTarget.classList.remove("pointer-events-none", "opacity-0");
    requestAnimationFrame(() => {
      // wait for the overlay to become un-hidden
      this.iosPanelTarget.classList.remove("translate-y-full");
    });
    setTimeout(() => {
      this.iosCloseButtonTarget.classList.remove("hidden");
    }, this.animationDurationValue);
  }

  closeiOSGuide() {
    log("Closing iOS installation guide");
    this.iosCloseButtonTarget.classList.add("hidden");
    this.iosPanelTarget.classList.add("translate-y-full");
    setTimeout(() => {
      this.iosOverlayTarget.classList.add("opacity-0", "pointer-events-none");
    }, this.animationDurationValue);
  }

  #showAndroidNonChromeGuide() {
    log("Showing Non-Chrome Android guide");
    this.androidNonChromeOverlayTarget.classList.remove("pointer-events-none", "opacity-0");
    requestAnimationFrame(() => {
      // wait for the overlay to become un-hidden
      this.androidNonChromePanelTarget.classList.remove("translate-y-full");
    });
    setTimeout(() => {
      this.androidNonChromeCloseButtonTarget.classList.remove("hidden");
    }, this.animationDurationValue);
  }

  closeAndroidNonChromeGuide() {
    log("Closing Non-Chrome Android guide");
    this.androidNonChromeCloseButtonTarget.classList.add("hidden");
    this.androidNonChromePanelTarget.classList.add("translate-y-full");
    setTimeout(() => {
      this.androidNonChromeOverlayTarget.classList.add("opacity-0", "pointer-events-none");
    }, this.animationDurationValue);
  }

  #checkForInstallationAndroid() {
    log("Starting installation check");
    // Check every second for a while to see if the PWA has been installed
    let checkCount = 0;
    const checkInterval = setInterval(async () => {
      checkCount++;
      log(`Check ${checkCount} for installation...`);

      if (await isPwaInstalled(log)) {
        log("PWA installation detected");
        clearInterval(checkInterval);
        setTimeout(async () => {
          // Wait for a few seconds before attempting to open. Seems to not work otherwise.
          this.installContainerTarget.classList.remove("hidden");
          this.androidPostInstallBannerTarget.classList.add("hidden");
          this.openAppBannerTarget.classList.remove("hidden");
        }, 6000);
      } else if (checkCount >= 30) {
        log("Installation check timed out");
        clearInterval(checkInterval);
      }
    }, 1000);
  }
}
